import { useCallback, type ComponentProps, useMemo } from "react";
import {
  Link as RemixLink,
  useResolvedPath,
  useNavigate,
  resolvePath,
  useLocation,
} from "@remix-run/react";
import type { Path, To } from "react-router";
import { isDefaultLocale, useLocale } from "~/i18n/i18n.client";

import { i18nConfig } from "~/i18n/config";
import { trimLeadingSlash } from "~/lib/utils/helper/path";

export function LocaleLink(props: ComponentProps<typeof RemixLink>) {
  const localePath = useGetLocalePath(props.to, { relative: props.relative });
  return <RemixLink {...props} to={localePath} />;
}

export function useGetLocalePath(
  to: To,
  options: Parameters<typeof useResolvedPath>[1]
) {
  const { pathname } = useLocation();
  const locale = useLocale();
  const resolvedPath = useResolvedPath(to, options);
  return useMemo(
    () => getLocalePath(resolvedPath, locale, pathname),
    [resolvedPath, locale, pathname]
  );
}

export function useLocationWithoutLocale(): ReturnType<typeof useLocation> {
  const location = useLocation();
  const locale = useLocale();
  return useMemo(
    () => ({
      ...location,
      pathname: location.pathname.replace("/" + locale, "") || "/",
    }),
    [location, locale]
  );
}

export function usePathnameWithoutLocale(): string {
  return useLocationWithoutLocale().pathname;
}

type NavigateFunction = ReturnType<typeof useNavigate>;
type NavigateOptions = NavigateFunction extends {
  (...args: infer Params): any;
  (...args: any): any;
}
  ? NonNullable<Params[1]>
  : any;

export function useLocaleNavigate() {
  const { pathname } = useLocation();
  const locale = useLocale();
  const navigate = useNavigate();

  const localeNavigate = useCallback(
    (to: To | number, options?: NavigateOptions & { locale?: string }) => {
      if (typeof to === "number") {
        // delta
        return navigate(to);
      }
      if (typeof to === "object" && to.pathname === undefined) {
        // no `pathname` provided .eg query/hash navigate
        return navigate(to);
      }
      // create new path with locale prefixed `pathname`
      const localePath = getLocalePath(
        resolvePath(to, pathname),
        options?.locale ?? locale,
        pathname
      );
      return navigate(localePath, options);
    },
    [navigate, locale, pathname]
  );
  return localeNavigate;
}

function getLocalePath(path: Path, locale: string, currentPath?: string): Path {
  if (
    typeof currentPath === "string" &&
    !isPathHasLocale(currentPath) &&
    isDefaultLocale(locale)
  ) {
    // noop if current path has no locale prefix AND we're navigating to "default" locale.
    // .eg calling `navigate("/en-US/login")` from `/` should go to `/login`,
    // (because "en-US" is default locale)
    // or having `<LocaleLink to="/login">` on `/` should make it `/login` not `/en-US/login`.
    return path;
  }
  return locale && i18nConfig.supportedLngs.includes(locale)
    ? { ...path, pathname: addLocaleToPath(path.pathname, locale) }
    : path;
}

function addLocaleToPath(urlPath: string, locale: string) {
  return isPathHasLocale(urlPath)
    ? urlPath
    : `/${locale}/${trimLeadingSlash(urlPath)}`;
}

function isPathHasLocale(urlPath: string) {
  const lowerCasedPath = urlPath.toLowerCase();
  return i18nConfig.supportedLngs.some((locale) =>
    lowerCasedPath.startsWith("/" + locale.toLowerCase())
  );
}
